import React, { useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { radius, colors, spacer, shadow, fontSize, rgba, snippet, media, fonts, fontWeight } from 'settings/style';
import { Container, Spinner, PaginatingItems } from 'components/elements';
import { useCategoriesQuery, useGetAllGroupsLazyQuery } from 'graphql/types';
import { Typography } from 'antd';
import { RouteComponentProps, Link, useHistory } from 'react-router-dom';
import { LeftOutlined } from '@ant-design/icons';
import GroupCard from 'components/groupCard';

const { Paragraph } = Typography;

type MatchParams = { id: string };
type CategoryProps = RouteComponentProps<MatchParams>;

const pageSize = 18;

const SingleCategory = (props: CategoryProps) => {
  const {
    match: {
      params: { id },
    },
  } = props;

  const history = useHistory();
  const { data } = useCategoriesQuery();

  const [getGroups, { data: groupResult, loading: groupLoading }] = useGetAllGroupsLazyQuery();

  const getGroupPage = useCallback(
    (page: number) => {
      getGroups({
        variables: {
          pagination: { page, pageSize },
          sort: 'createdAt:desc',
          filters: { categories: { id: { in: [id] } } },
        },
      });
    },
    [getGroups, id],
  );

  useEffect(() => {
    getGroupPage(1);
  }, [getGroupPage]);

  const groupsData = groupResult?.groups.data || [];
  const pagination = groupResult?.groups?.meta?.pagination;

  const selectedCategory = useMemo(() => {
    if (data && data.categories) {
      return data.categories.data.find((category) => String(category.id) === String(id));
    }
    return null;
  }, [data, id]);

  const subCategories = useMemo(() => {
    if (selectedCategory) {
      return selectedCategory.attributes.subCategories.data
        .map((item) => data.categories.data.find((cat) => String(cat.id) === String(item.id)))
        .sort((a, b) => (a.attributes.order > b.attributes.order ? 1 : -1));
    }
    return [];
  }, [selectedCategory, data]);

  return (
    <CategoriesWrapper>
      <Container>
        {selectedCategory ? (
          <div>
            <CategoryBackRow>
              <CategoryBackButton type="button" onClick={() => history.goBack()}>
                <LeftOutlined />
                Back
              </CategoryBackButton>
            </CategoryBackRow>
            <p className="headTitle">{selectedCategory.attributes.title}</p>
            <SubCategoriesGrid>
              {subCategories.map((category) => (
                <CategoriesItem
                  key={category.attributes.slug}
                  img={category.attributes.coverPhotoUrl || ''}
                  to={`/category/${category.id}`}
                >
                  <div className="img-container">
                    <div className="title">{category.attributes.title} </div>
                    <Paragraph ellipsis={{ rows: 7, expandable: false }}>{category.attributes.description}</Paragraph>
                  </div>
                </CategoriesItem>
              ))}
            </SubCategoriesGrid>
            <p className="headTitle">Related Groups</p>
            <PaginatingItems
              items={groupsData}
              loading={groupLoading}
              renderList={GroupCard}
              perView={3}
              pageSize={pagination?.pageSize}
              total={pagination?.total}
              current={pagination?.page}
              onChange={(page) => getGroupPage(page)}
            />
          </div>
        ) : (
          <Spinner />
        )}
      </Container>
    </CategoriesWrapper>
  );
};

export default SingleCategory;

/** Block wrapper so .headTitle (inline-block) does not sit on the same line as the back control */
const CategoryBackRow = styled.div`
  display: block;
  width: 100%;
`;

const CategoryBackButton = styled.button`
  border: 0;
  background: transparent;
  color: ${colors.grey900};
  font-size: 11px;
  font-weight: ${fontWeight.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  margin: ${spacer.lg} 0 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;

  &:hover {
    color: ${colors.accent};
  }
`;

const CategoriesWrapper = styled.div`
  padding: ${spacer.lg};
  .headTitle {
    font-family: ${fonts.tertiary};
    font-size: ${fontSize.xl};
    ${snippet.underline()}
    margin-top: ${spacer.lg};
  }
  .description {
    margin-bottom: ${spacer.lg};
    font-size: ${fontSize.md};
  }
  .exploreButton {
    margin-bottom: ${spacer.lg};
  }
`;

const SubCategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: ${spacer.md};
  margin-bottom: ${spacer.lg};

  ${media.md`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}

  ${media.xs`
    grid-template-columns: 1fr;
  `}
`;

interface CategoriesItemProps {
  img: string;
}

const CategoriesItem = styled(Link) <CategoriesItemProps>`
  height: 200px;
  width: 100%;
  border-radius: ${radius.lg};
  font-size: ${fontSize.sm};
  color: ${colors.white};
  box-shadow: ${shadow.md};
  overflow: hidden;
  margin: 0;
  .ant-typography {
    color: ${colors.white};
  }
  .img-container {
    height: 100%;
    width: 100%;
    background-image: url(${({ img }) => img});
    background-repeat: no-repeat, repeat;
    display: flex;
    justify-content: center;
    align-items: center;
    background-size: cover;
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    .title {
      position: absolute;
      padding: ${spacer.sm} ${spacer.md};
      background-color: ${rgba(colors.gray600, 0.9)};
      border-radius: ${radius.xl};
    }
    .ant-typography {
      transition: 0.4s ease;
      padding: ${spacer.xl};
      opacity: 0;
      background-color: rgba(26, 26, 26, 0.68);
      width: 100%;
      height: 100%;
      margin: 0;
    }
  }
  &:hover {
    .title {
      display: none;
    }
    .ant-typography {
      opacity: 1;
    }
  }
`;
